import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import CtmsWorkspaceModal from "../../common/ctms/CtmsWorkspaceModal";
import ConfirmModal from "../../common/ConfirmModal";
import AuthModal from "../../common/authmodal/authmodal";
import DropdownMenu from "../../common/DropDownMenu/dropdownmenu";
import AdminChangeUserCredentialsModal from "../CredentialPolicy/AdminChangeUserCredentialsModal";
import {
  adminChangeUserAccessCode,
  adminChangeUserPassword,
  parseAdminCredentialChangeResponse,
} from "../../../services/credentialPolicyService";
import ExternalAssignedStudiesPanel from "./ExternalAssignedStudiesPanel";
import ExternalAccessActivityLog from "./ExternalAccessActivityLog";
import UserProfile from "./userProfile";
import SponsorCroUserAddModal from "./SponsorCroUserAddModal";
import {
  useExternalUserAccessScoping,
  parseCommaSeparatedIds,
  parseExternalEntityAccessRows,
  extractExternalEntityAccessFromProfile,
  fetchExternalEntityAccessGrants,
  documentRowsToDocumentsXml,
  documentRowsToExternalStaffIsfDocuments,
  externalEntityAccessToDocumentRows,
  mergeDocumentRowsWithDelta,
} from "./ExternalUserAccessScoping";
import {
  formatUserTypeLabel,
  isSponsorCroUserRecord,
  isSponsorOrCroRoleLabel,
} from "../../../constants/userTypeLabels";
import { organizationHasSponsorCroManagementFeature } from "../../../utils/orgModuleAccess";
import {
  fetchUserList,
  mapUsersForTable,
  parseUserListResponse,
} from "../../../services/userManagementService";
import { fetchStudyMgmtList } from "../studymanagement/studyMgmtListService";
import {
  buildStudyColorLookup,
  pickStudyColorRaw,
} from "../../../utils/studyColorUtils";
import { normListId } from "../../common/dualListBox";
import utils from "../../../utils/utils";
import {
  parsePermissions,
  hasPermission,
} from "../../../utils/permissionUtils";
import {
  validateNationalMobile,
  sanitizeNationalMobileInput,
} from "../../../utils/siteFieldValidationUtils";
import "./CroStudyAssignment.scss";

const DEFAULT_AVATAR = "../images/avatar.png";

const isProfilePictureDataUrl = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .startsWith("data:image");

const withProfilePictureCacheBust = (url) => {
  const raw = String(url ?? "").trim();
  if (!raw || isProfilePictureDataUrl(raw)) return raw;
  if (raw.toLowerCase().includes("avatar.png")) return raw;
  const sep = raw.includes("?") ? "&" : "?";
  return `${raw}${sep}_t=${Date.now()}`;
};

const hasUserProfilePicture = (row) => {
  const url = String(row?.ProfilePictureUrl ?? "").trim();
  if (!url) return false;
  if (url.toLowerCase().includes("avatar.png")) return false;
  return true;
};

const TYPE_FILTER_ALL = "all";
const TYPE_FILTER_SPONSOR = "sponsor";
const TYPE_FILTER_CRO = "cro";

function isStaffProfileActive(staff) {
  if (!staff) return true;
  const archived = staff.Archived ?? staff.Inactive ?? staff.inactive;
  if (
    archived === 1 ||
    archived === true ||
    archived === "1" ||
    String(archived).trim().toLowerCase() === "yes"
  ) {
    return false;
  }
  const status = String(staff.Status ?? staff.AccountStatus ?? "")
    .trim()
    .toLowerCase();
  if (
    status === "inactive" ||
    status === "blocked" ||
    status === "archived"
  ) {
    return false;
  }
  return true;
}

function getUserMgmtStatusLabel(row) {
  const statusRaw = String(row?.Status ?? "").trim();
  if (statusRaw) {
    const n = statusRaw.toLowerCase();
    if (n === "active") return "Active";
    if (n === "inactive" || n === "blocked" || n === "archived") {
      return "Inactive";
    }
    return statusRaw;
  }
  const inactiveFlag = row?.Inactive ?? row?.InActive ?? row?.inactive;
  if (inactiveFlag === 1 || inactiveFlag === true || inactiveFlag === "1") {
    return "Inactive";
  }
  return "Active";
}

function pickStudyIdFromRow(s) {
  if (!s || typeof s !== "object") return "";
  return s.StudyId ?? s.studyId ?? s.value ?? s.StudyID ?? "";
}

function collectAssignedStudyRowsFromProfile(data, staff) {
  const buckets = [
    data?.StudyList,
    data?.AssignedStudyList,
    data?.UserStudyList,
    data?.StaffStudyList,
    staff?.StudyList,
    staff?.AssignedStudyList,
  ];
  for (const bucket of buckets) {
    if (Array.isArray(bucket) && bucket.length) return bucket;
  }
  return [];
}

function formatDisplayName(row) {
  const name = String(row?.Name ?? "").trim();
  if (name) return name;
  const first = String(row?.Forename ?? row?.FirstName ?? "").trim();
  const last = String(row?.Surname ?? row?.LastName ?? "").trim();
  return [first, last].filter(Boolean).join(" ").trim() || "—";
}

function formatRoleLabel(row) {
  return String(row?.Roles ?? row?.Role ?? "").trim() || "—";
}

function detectSponsorCroKind(row) {
  const role = formatRoleLabel(row).toLowerCase();
  if (role === "sponsor") return TYPE_FILTER_SPONSOR;
  if (role === "cro") return TYPE_FILTER_CRO;
  const raw = row?.IsCRO ?? row?.IsCro;
  if (raw === 2 || raw === "2") return TYPE_FILTER_CRO;
  if (raw === 1 || raw === "1") return TYPE_FILTER_SPONSOR;
  return TYPE_FILTER_SPONSOR;
}

function formatDate(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function peekStructuredExternalDocs(v) {
  if (v == null) return null;
  if (Array.isArray(v)) return v.length ? v : null;
  if (typeof v === "object") {
    const sid = v.StudyId ?? v.studyId;
    if (sid != null && String(sid).trim() !== "") return [v];
  }
  return null;
}

function CroSponsorUserListItem({
  row,
  selected,
  onSelect,
  menuItems = [],
}) {
  const menuTriggerRef = useRef(null);
  const retriedRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const remoteSrc = String(row?.ProfilePictureUrl ?? "").trim();
  const [imgSrc, setImgSrc] = useState(remoteSrc || DEFAULT_AVATAR);

  const name = formatDisplayName(row);
  const emp = String(row?.EmployeeNum ?? "").trim();
  const role = formatRoleLabel(row);
  const roleLabel = isSponsorOrCroRoleLabel(role)
    ? role
    : formatUserTypeLabel(row?.IsCRO ?? row?.IsCro);
  const status = getUserMgmtStatusLabel(row);
  const inactive = status !== "Active";
  const titleName = emp ? `(${emp}) ${name}` : name;

  const resolvedMenuItems = useMemo(
    () =>
      (menuItems || [])
        .filter((item) => {
          if (item?.divider) return true;
          if (typeof item.visible === "function") return item.visible(row);
          if (item.visible === false) return false;
          return true;
        })
        .map((item) => {
          if (item?.divider) return { divider: true };
          return {
            ...item,
            label:
              typeof item.label === "function" ? item.label(row) : item.label,
            icon: typeof item.icon === "function" ? item.icon(row) : item.icon,
            onClick: () => {
              setMenuOpen(false);
              item.onClick?.(row);
            },
          };
        }),
    [menuItems, row],
  );

  useEffect(() => {
    retriedRef.current = false;
    setImgSrc(remoteSrc || DEFAULT_AVATAR);
  }, [remoteSrc, row?.StaffId]);

  const handleImgError = (event) => {
    const failedSrc = String(event?.currentTarget?.src || "");
    if (
      !retriedRef.current &&
      remoteSrc &&
      !isProfilePictureDataUrl(remoteSrc) &&
      hasUserProfilePicture({ ProfilePictureUrl: remoteSrc }) &&
      !failedSrc.includes("_t=")
    ) {
      retriedRef.current = true;
      setImgSrc(withProfilePictureCacheBust(remoteSrc));
      return;
    }
    if (event?.currentTarget) {
      event.currentTarget.src = DEFAULT_AVATAR;
    }
    setImgSrc(DEFAULT_AVATAR);
  };

  return (
    <div
      className={`cro-study-assignment__user-row${
        selected ? " is-selected" : ""
      }${inactive ? " is-inactive" : ""}`}
    >
      <button
        type="button"
        className="cro-study-assignment__user-btn"
        onMouseDown={(event) => {
          // Avoid browser scroll-into-view jump when focusing the card.
          event.preventDefault();
        }}
        onClick={() => onSelect?.(row)}
        title={titleName}
      >
        <span className="cro-study-assignment__avatar-wrap">
          <img
            src={imgSrc}
            alt=""
            className="cro-study-assignment__avatar"
            onError={handleImgError}
          />
        </span>
        <span className="cro-study-assignment__user-text">
          <span className="cro-study-assignment__user-name">{name}</span>
          <span className="cro-study-assignment__user-meta">
            {emp ? (
              <span className="cro-study-assignment__emp-badge" title={emp}>
                {emp}
              </span>
            ) : null}
            <span
              className="cro-study-assignment__badge cro-study-assignment__badge--role"
              title={roleLabel}
            >
              {roleLabel}
            </span>
            <span
              className={`cro-study-assignment__badge${
                inactive
                  ? " cro-study-assignment__badge--inactive"
                  : " cro-study-assignment__badge--active"
              }`}
              title={status}
            >
              {status}
            </span>
          </span>
        </span>
      </button>

      {resolvedMenuItems.length > 0 ? (
        <div className="cro-study-assignment__user-menu">
          <button
            ref={menuTriggerRef}
            type="button"
            className="cro-study-assignment__user-menu-trigger"
            aria-label={`Actions for ${name}`}
            aria-expanded={menuOpen}
            aria-haspopup="true"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setMenuOpen((prev) => !prev);
            }}
          >
            <i className="fas fa-ellipsis-v" aria-hidden="true" />
          </button>
          <DropdownMenu
            show={menuOpen}
            onClose={() => setMenuOpen(false)}
            trigger={menuTriggerRef}
            align="end"
            items={resolvedMenuItems}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Admin screen to assign study / site / eISF–ePSF access for Sponsor/CRO users
 * without opening each user profile.
 */
export default function CroStudyAssignment() {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [searchParams, setSearchParams] = useSearchParams();

  const isSystemAdmin = userData?.Role === "System Administrator";
  const userPermissions = isSystemAdmin
    ? []
    : parsePermissions(userData?.Permission || "");
  const canManage =
    isSystemAdmin ||
    hasPermission(userPermissions, "SPONSOR_CRO_MANAGEMENT");
  const featureEnabled = organizationHasSponsorCroManagementFeature(userData);
  // On this screen, Sponsor/CRO management permission includes changing those users' credentials.
  const canChangeCredentials = canManage;

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [listQuery, setListQuery] = useState("");
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [modalAssignStaffIds, setModalAssignStaffIds] = useState([]);
  const [statusConfirm, setStatusConfirm] = useState({
    open: false,
    action: null,
    row: null,
  });
  const [statusAuthOpen, setStatusAuthOpen] = useState(false);
  const [pendingStatusToggle, setPendingStatusToggle] = useState(null);
  const [adminCredForm, setAdminCredForm] = useState({
    open: false,
    row: null,
  });
  const [adminCredConfirm, setAdminCredConfirm] = useState({
    open: false,
    mode: null,
    row: null,
    value: "",
  });
  const [adminCredAuthOpen, setAdminCredAuthOpen] = useState(false);
  const [pendingAdminCred, setPendingAdminCred] = useState(null);
  const [studyList, setStudyList] = useState([]);
  const [studyListReady, setStudyListReady] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isProfileUserActive, setIsProfileUserActive] = useState(true);
  const [selectedUserMeta, setSelectedUserMeta] = useState(null);
  const [studies, setStudies] = useState([]);
  const [staffSnapshot, setStaffSnapshot] = useState(null);
  const [externalIsfStudiesCsv, setExternalIsfStudiesCsv] = useState("");
  const [externalIsfDocumentsCsv, setExternalIsfDocumentsCsv] = useState("");
  const [externalStaffDocumentsXml, setExternalStaffDocumentsXml] =
    useState("");
  const [externalStaffIsfDocumentsFromApi, setExternalStaffIsfDocumentsFromApi] =
    useState(null);
  const [externalEntityAccessFromApi, setExternalEntityAccessFromApi] =
    useState(null);
  const [assignedTableBootstrapping, setAssignedTableBootstrapping] =
    useState(false);
  const [externalShareHydrationNonce, setExternalShareHydrationNonce] =
    useState(0);
  const [assignRequestNonce, setAssignRequestNonce] = useState(0);
  const [profileModalStaffId, setProfileModalStaffId] = useState("");
  const [detailTab, setDetailTab] = useState("studies"); // studies | activity
  const [activityLogNonce, setActivityLogNonce] = useState(0);

  const loadSeqRef = useRef(0);
  const studyOptionsCacheRef = useRef(null);
  const scopingResetRef = useRef(null);
  const listBodyRef = useRef(null);
  const listScrollTopRef = useRef(0);

  const externalScoping = useExternalUserAccessScoping({
    organizationId: userData?.OrganizationId,
    staffId: userData?.StaffId,
    studyOptions: studyList,
    showAlert,
    studies,
    onStudiesChange: (updater) =>
      setStudies((prev) =>
        typeof updater === "function" ? updater(prev || []) : updater,
      ),
    readOnly: !canManage || (Boolean(selectedStaffId) && !isProfileUserActive),
    highlightedStudyIdsCsv: externalIsfStudiesCsv,
    serverDocumentIdsCsv: externalIsfDocumentsCsv,
    serverDocumentsXml: externalStaffDocumentsXml,
    serverExternalStaffIsfDocuments: externalStaffIsfDocumentsFromApi,
    serverEntityAccess: externalEntityAccessFromApi,
    tableBootstrapping: assignedTableBootstrapping,
    hydrationNonce: selectedStaffId ? externalShareHydrationNonce : 0,
  });

  scopingResetRef.current = externalScoping.reset;

  const breadcrumbItems = useMemo(
    () => [
      { label: "Home", path: "/notification-center" },
      { label: "Sponsor/CRO management" },
    ],
    [],
  );

  const resetSelectedUserState = useCallback(() => {
    setStudies([]);
    setStaffSnapshot(null);
    setSelectedUserMeta(null);
    setIsProfileUserActive(true);
    setExternalIsfStudiesCsv("");
    setExternalIsfDocumentsCsv("");
    setExternalStaffDocumentsXml("");
    setExternalStaffIsfDocumentsFromApi(null);
    setExternalEntityAccessFromApi(null);
    setAssignedTableBootstrapping(false);
    setExternalShareHydrationNonce(0);
    scopingResetRef.current?.();
  }, []);

  const loadStudyOptions = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return [];
    if (studyOptionsCacheRef.current) {
      setStudyList(studyOptionsCacheRef.current);
      setStudyListReady(true);
      return studyOptionsCacheRef.current;
    }
    try {
      const [response, mgmtStudyRows] = await Promise.all([
        api.post(API_ENDPOINTS.USER.GET_USER_PROFILE_COMMONDATA, {
          OrganizationId: userData.OrganizationId,
          StaffIDRef: userData.StaffId,
          CreatedBy: userData.StaffId,
          PageName: "UserProfile",
        }),
        fetchStudyMgmtList({
          organizationId: userData.OrganizationId,
          staffIdRef: userData.StaffId,
          createdBy: userData.StaffId,
          action: "GetStudyMgmtLiveList",
        }).catch(() => []),
      ]);
      if (Number(response?.success) !== 1) {
        setStudyList([]);
        setStudyListReady(true);
        return [];
      }
      const studyColorLookup = buildStudyColorLookup(mgmtStudyRows);
      const studyData = (response.data?.StudyList || []).map((s) => {
        const num = s?.StudyNumber || s?.StudyCode || s?.StudyNo || "";
        const name = s?.StudyName || "";
        const label = num ? `${num} - ${name}` : name;
        const color =
          pickStudyColorRaw(s) ||
          studyColorLookup.get(normListId(s?.StudyId)) ||
          "";
        return {
          label,
          value: s?.StudyId,
          studyNumber: num,
          studyName: name,
          studyColor: color,
          StudySchColor: color,
          StudyColor: s?.StudyColor || color,
        };
      });
      studyOptionsCacheRef.current = studyData;
      setStudyList(studyData);
      setStudyListReady(true);
      return studyData;
    } catch {
      setStudyList([]);
      setStudyListReady(true);
      return [];
    }
  }, [userData?.OrganizationId, userData?.StaffId]);

  const loadCroUsers = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    try {
      setUsersLoading(true);
      const response = await fetchUserList({
        organizationId: userData.OrganizationId,
        staffId: userData.StaffId,
      });
      const parsed = parseUserListResponse(response);
      if (!parsed.ok) {
        setUsers([]);
        showAlert({
          type: "error",
          title: "Unable to load users",
          message: parsed.message,
        });
        return;
      }
      const croUsers = mapUsersForTable(parsed.users).filter((user) =>
        isSponsorCroUserRecord(user?.IsCRO ?? user?.IsCro),
      );
      croUsers.sort((a, b) =>
        formatDisplayName(a).localeCompare(formatDisplayName(b), undefined, {
          sensitivity: "base",
        }),
      );
      setUsers(croUsers);
    } catch (error) {
      setUsers([]);
      showAlert({
        type: "error",
        title: "Unable to load users",
        message:
          error?.message ||
          "We could not load Sponsor/CRO users. Please try again.",
      });
    } finally {
      setUsersLoading(false);
    }
  }, [showAlert, userData?.OrganizationId, userData?.StaffId]);

  const loadSelectedUser = useCallback(
    async (staffId) => {
      if (!staffId || !userData?.OrganizationId || !userData?.StaffId) return;
      const seq = ++loadSeqRef.current;
      setProfileLoading(true);
      setAssignedTableBootstrapping(true);
      setExternalIsfStudiesCsv("");
      setExternalIsfDocumentsCsv("");
      setExternalStaffDocumentsXml("");
      setExternalStaffIsfDocumentsFromApi(null);
      setExternalEntityAccessFromApi(null);
      setExternalShareHydrationNonce(0);
      scopingResetRef.current?.();

      try {
        const studyOptions =
          studyOptionsCacheRef.current || (await loadStudyOptions());
        const userResponse = await api.post(
          API_ENDPOINTS.USER.GET_INDIVIDUAL_USER,
          {
            OrganizationId: userData.OrganizationId,
            StaffIDRef: userData.StaffId,
            CreatedBy: userData.StaffId,
            StaffId: staffId,
            Action: "GetProfileDetails",
          },
        );
        if (seq !== loadSeqRef.current) return;

        const staff = Array.isArray(userResponse?.data?.StaffRecord)
          ? userResponse.data.StaffRecord[0]
          : userResponse?.data?.StaffRecord ||
            userResponse?.data?.SuccessStory?.[0] ||
            null;
        if (!staff || !isSponsorCroUserRecord(staff?.IsCRO ?? staff?.IsCro)) {
          showAlert({
            type: "warning",
            title: "Not a Sponsor/CRO user",
            message:
              "This account is not a Sponsor/CRO user, so study sharing cannot be managed here.",
          });
          setSelectedStaffId("");
          resetSelectedUserState();
          return;
        }

        const active = isStaffProfileActive(staff);
        setIsProfileUserActive(active);

        const listRow = users.find(
          (u) =>
            String(u?.StaffId || "").toLowerCase() ===
            String(staffId).toLowerCase(),
        );
        const fullName = `${staff.Forename || ""} ${staff.Surname || ""}`.trim();
        setSelectedUserMeta({
          staffId,
          name: fullName || formatDisplayName(listRow) || "—",
          employeeNumber: String(
            staff["Employee Number"] ?? listRow?.EmployeeNum ?? "",
          ).trim(),
          email: String(staff["Email Id"] ?? listRow?.EmailId ?? "").trim(),
          role: String(staff.Roles ?? listRow?.Roles ?? "").trim() || "—",
          kind: detectSponsorCroKind({
            ...listRow,
            Roles: staff.Roles ?? listRow?.Roles,
            IsCRO: staff.IsCRO ?? staff.IsCro ?? listRow?.IsCRO,
          }),
          status: active ? "Active" : "Inactive",
        });

        const extStudiesRaw =
          staff?.ExternalStaffISFStudies ??
          staff?.externalStaffISFStudies ??
          userResponse.data?.ExternalStaffISFStudies ??
          "";
        const extDocsFromData = userResponse.data?.ExternalStaffISFDocuments;
        const extDocsFromStaff =
          staff?.ExternalStaffISFDocuments ??
          staff?.externalStaffISFDocuments;
        const structuredExternalDocs =
          peekStructuredExternalDocs(extDocsFromData) ??
          peekStructuredExternalDocs(extDocsFromStaff);
        const extDocsRaw =
          structuredExternalDocs != null
            ? ""
            : extDocsFromStaff ?? extDocsFromData ?? "";
        const documentsXmlRaw =
          staff?.DocumentsXML ??
          staff?.documentsXML ??
          staff?.DocumentsXml ??
          userResponse.data?.DocumentsXML ??
          userResponse.data?.documentsXML ??
          "";

        let entityAccessRaw = extractExternalEntityAccessFromProfile(
          userResponse.data,
          staff,
        );
        try {
          const liveGrants = await fetchExternalEntityAccessGrants({
            organizationId: userData.OrganizationId,
            staffId,
          });
          if (parseExternalEntityAccessRows(liveGrants)?.length) {
            entityAccessRaw = liveGrants;
          }
        } catch {
          // Keep profile-attached grants when GetValid is not deployed yet.
        }
        if (seq !== loadSeqRef.current) return;

        const parsedEntityAccess =
          parseExternalEntityAccessRows(entityAccessRaw) || [];
        const extStudyIdTokens = parseCommaSeparatedIds(
          typeof extStudiesRaw === "string"
            ? extStudiesRaw
            : String(extStudiesRaw ?? ""),
        );
        const generalStudyList = collectAssignedStudyRowsFromProfile(
          userResponse.data,
          staff,
        );
        const mergedStudyNormIds = [
          ...new Set([
            ...generalStudyList
              .map((s) => normListId(pickStudyIdFromRow(s)))
              .filter(Boolean),
            ...extStudyIdTokens.map((t) => normListId(t)).filter(Boolean),
            ...parsedEntityAccess
              .map((row) => normListId(row.studyId))
              .filter(Boolean),
          ]),
        ];

        const studiesForForm =
          mergedStudyNormIds.length > 0
            ? mergedStudyNormIds.map((id) => {
                const opt = (studyOptions || []).find(
                  (s) => normListId(s.value) === id,
                );
                const fromGeneral = generalStudyList.find(
                  (s) => normListId(pickStudyIdFromRow(s)) === id,
                );
                const sid =
                  opt?.value ?? pickStudyIdFromRow(fromGeneral) ?? id;
                const studyNumber =
                  opt?.studyNumber ||
                  fromGeneral?.StudyNumber ||
                  fromGeneral?.studyNumber ||
                  "";
                const studyName =
                  opt?.studyName ||
                  fromGeneral?.StudyName ||
                  fromGeneral?.studyName ||
                  "";
                const label =
                  opt?.label ||
                  (studyNumber
                    ? `${studyNumber} - ${studyName}`
                    : studyName || fromGeneral?.label || String(sid));
                const color =
                  opt?.studyColor ||
                  opt?.StudySchColor ||
                  fromGeneral?.studyColor ||
                  fromGeneral?.StudySchColor ||
                  "";
                return {
                  StudyId: sid,
                  label,
                  studyNumber,
                  studyName,
                  StudyNumber: studyNumber,
                  StudyName: studyName,
                  studyColor: color,
                  StudySchColor: color,
                };
              })
            : generalStudyList;

        const mobileCountryIso2 = String(staff.MobileCountryIso2 || "GB")
          .trim()
          .toUpperCase();
        const snapshot = {
          firstName: staff.Forename?.trim() || "",
          lastName: staff.Surname?.trim() || "",
          employeeNumber: String(staff["Employee Number"] ?? "").trim(),
          email: String(staff["Email Id"] ?? "").trim(),
          contactNumber: sanitizeNationalMobileInput(
            staff["Contact Number"] || "",
            mobileCountryIso2,
          ),
          mobileCountryIso2,
          gender: staff.Gender || staff.gender || "",
          startDate: staff["Start Date"] ? new Date(staff["Start Date"]) : null,
          accessDate: staff["Access Date"]
            ? new Date(staff["Access Date"])
            : null,
          dob: staff["Date of birth"]
            ? new Date(utils.parseDDMMYYYY(staff["Date of birth"]))
            : null,
          roleIdRef: String(staff.RoleIdRef || "").trim(),
          departmentId: String(staff.DepartmentId || "").trim(),
          lineManagerId: String(staff.LineManagerId || "").trim(),
          haveLineManagerRights:
            String(staff["Line Manager Rights"] || "")
              .trim()
              .toLowerCase() === "yes",
          addressLine1: staff["H No"] || "",
          city: staff.City || "",
          postalCode: staff["Post Code"] || "",
          county: staff.County || "",
          country: staff.Country || "",
          profilePicture: staff.ProfilePictureUrl || "",
          IsCRO: 1,
          studies: studiesForForm,
        };

        setStaffSnapshot(snapshot);
        setStudies(studiesForForm);
        setExternalEntityAccessFromApi(entityAccessRaw);
        setExternalIsfStudiesCsv(
          typeof extStudiesRaw === "string"
            ? extStudiesRaw
            : String(extStudiesRaw ?? ""),
        );
        if (structuredExternalDocs) {
          setExternalStaffIsfDocumentsFromApi(structuredExternalDocs);
          setExternalIsfDocumentsCsv("");
        } else {
          setExternalStaffIsfDocumentsFromApi(null);
          setExternalIsfDocumentsCsv(
            typeof extDocsRaw === "string"
              ? extDocsRaw
              : String(extDocsRaw ?? ""),
          );
        }
        setExternalStaffDocumentsXml(
          typeof documentsXmlRaw === "string"
            ? documentsXmlRaw
            : String(documentsXmlRaw ?? ""),
        );
        setExternalShareHydrationNonce((n) => n + 1);
      } catch (error) {
        if (seq !== loadSeqRef.current) return;
        console.error("loadSelectedUser", error);
        showAlert({
          type: "error",
          title: "Unable to load assignments",
          message:
            error?.message ||
            "We could not load this user's study sharing. Please try again.",
        });
        resetSelectedUserState();
      } finally {
        if (seq === loadSeqRef.current) {
          setProfileLoading(false);
          setAssignedTableBootstrapping(false);
        }
      }
    },
    [
      loadStudyOptions,
      resetSelectedUserState,
      showAlert,
      userData?.OrganizationId,
      userData?.StaffId,
      users,
    ],
  );

  useEffect(() => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    loadCroUsers();
    loadStudyOptions();
  }, [
    loadCroUsers,
    loadStudyOptions,
    userData?.OrganizationId,
    userData?.StaffId,
  ]);

  // Sync selection FROM the URL only when the query (or user list) changes.
  // Do not depend on selectedStaffId — that raced with setSearchParams and
  // briefly re-applied the previous staffId (select A → flash B → back to A).
  useEffect(() => {
    const fromQuery = String(searchParams.get("staffId") || "").trim();
    if (!fromQuery || usersLoading || !users.length) return;
    const match = users.find(
      (u) =>
        String(u?.StaffId || "").toLowerCase() === fromQuery.toLowerCase(),
    );
    if (!match) return;
    const nextId = String(match.StaffId);
    setSelectedStaffId((prev) =>
      String(prev || "").toLowerCase() === nextId.toLowerCase() ? prev : nextId,
    );
  }, [searchParams, users, usersLoading]);

  useEffect(() => {
    if (!selectedStaffId) {
      resetSelectedUserState();
      return;
    }
    if (!studyListReady) return;
    loadSelectedUser(selectedStaffId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per selected staff + study list ready
  }, [selectedStaffId, studyListReady]);

  useEffect(() => {
    setAssignRequestNonce(0);
    setDetailTab("studies");
  }, [selectedStaffId]);

  const isListUserActive = useCallback((row) => {
    return getUserMgmtStatusLabel(row) === "Active";
  }, []);

  const filteredUsers = useMemo(() => {
    const q = listQuery.trim().toLowerCase();
    return users.filter((row) => {
      if (!q) return true;
      const hay = [formatDisplayName(row), row?.EmployeeNum]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [listQuery, users]);

  const activeUsersForAssign = useMemo(
    () => users.filter((row) => isListUserActive(row)),
    [users, isListUserActive],
  );

  const assignTargetStaffIds = useMemo(() => {
    const fromModal = (modalAssignStaffIds || [])
      .map((id) => String(id || "").trim())
      .filter(Boolean)
      .filter((id) =>
        activeUsersForAssign.some(
          (row) =>
            String(row?.StaffId || "").toLowerCase() === id.toLowerCase(),
        ),
      );
    if (fromModal.length) return fromModal;
    if (selectedStaffId && isProfileUserActive) {
      return [String(selectedStaffId)];
    }
    return [];
  }, [
    modalAssignStaffIds,
    activeUsersForAssign,
    selectedStaffId,
    isProfileUserActive,
  ]);

  const selectUser = useCallback(
    (staffId) => {
      const id = String(staffId || "").trim();
      if (!id) return;
      if (listBodyRef.current) {
        listScrollTopRef.current = listBodyRef.current.scrollTop;
      }
      setSelectedStaffId(id);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("staffId", id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useLayoutEffect(() => {
    if (!listBodyRef.current) return;
    listBodyRef.current.scrollTop = listScrollTopRef.current;
  }, [selectedStaffId]);

  const breadcrumbActions = useMemo(() => {
    if (!canManage) return [];
    return [
      {
        icon: "fas fa-plus",
        label: "Assign study",
        onClick: () => {
          setDetailTab("studies");
          setAssignRequestNonce((n) => n + 1);
        },
      },
    ];
  }, [canManage]);

  const breadcrumbPreActions = canManage ? (
    <button
      type="button"
      className="actions-trigger"
      onClick={() => setAddUserOpen(true)}
      title="Add Sponsor/CRO user"
    >
      <i className="fas fa-plus me-1" aria-hidden="true" />
      Add user
    </button>
  ) : null;

  const handleToggleStatusClick = useCallback((row) => {
    const action = isListUserActive(row) ? "deactivate" : "activate";
    setStatusConfirm({ open: true, action, row });
  }, [isListUserActive]);

  const handleStatusConfirmClose = () => {
    setStatusConfirm({ open: false, action: null, row: null });
  };

  const handleStatusConfirmYes = () => {
    const { action, row } = statusConfirm;
    setPendingStatusToggle({ action, row });
    setStatusAuthOpen(true);
    handleStatusConfirmClose();
  };

  const handleStatusAuthClose = () => {
    setStatusAuthOpen(false);
    setPendingStatusToggle(null);
  };

  const handleStatusAuthSubmit = async () => {
    if (!pendingStatusToggle?.row || !pendingStatusToggle?.action) return;
    const { row, action } = pendingStatusToggle;

    try {
      showLoader("Updating user status...");
      const response = await api.post(
        API_ENDPOINTS.USER.ACTIVATE_DEACTIVATE_USER,
        {
          OrganizationId: userData.OrganizationId,
          StaffId: row.StaffId,
          Action: action === "activate" ? "ActiveUser" : "BlockedUser",
          CreatedBy: userData.StaffId,
        },
      );

      setPendingStatusToggle(null);
      setStatusAuthOpen(false);
      await loadCroUsers();

      const tableMessage = response?.data?.Table?.[0]?.Message;
      const isSuccess = response?.success === 1;

      if (isSuccess) {
        showAlert({
          title: "Success",
          message:
            tableMessage ||
            response?.message ||
            "User status updated successfully.",
          type: "success",
        });
      } else {
        showAlert({
          title: "Error",
          message:
            tableMessage ||
            response?.message ||
            "Failed to update user status.",
          type: "danger",
        });
      }
    } catch (err) {
      console.error("Activate/Deactivate user error:", err);
      showAlert({
        title: "Error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update user status.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleAdminCredAction = useCallback((row) => {
    setAdminCredForm({ open: true, row });
  }, []);

  const handleAdminCredFormClose = () => {
    setAdminCredForm({ open: false, row: null });
  };

  const handleAdminCredFormSubmit = ({ mode, value }) => {
    const { row } = adminCredForm;
    handleAdminCredFormClose();
    setAdminCredConfirm({ open: true, mode, row, value });
  };

  const handleAdminCredConfirmClose = () => {
    setAdminCredConfirm({ open: false, mode: null, row: null, value: "" });
  };

  const handleAdminCredConfirmYes = () => {
    const { mode, row, value } = adminCredConfirm;
    setPendingAdminCred({ mode, row, value });
    handleAdminCredConfirmClose();
    setAdminCredAuthOpen(true);
  };

  const handleAdminCredAuthClose = () => {
    setAdminCredAuthOpen(false);
    setPendingAdminCred(null);
  };

  const handleAdminCredAuthSubmit = async () => {
    if (
      !pendingAdminCred?.row ||
      !userData?.OrganizationId ||
      !userData?.StaffId
    ) {
      return;
    }

    const { mode, row, value } = pendingAdminCred;
    const basePayload = {
      OrganizationId: userData.OrganizationId,
      StaffId: row.StaffId,
      CreatedBy: userData.StaffId,
    };

    try {
      showLoader(
        mode === "password"
          ? "Changing password..."
          : "Changing access code...",
      );
      const response =
        mode === "password"
          ? await adminChangeUserPassword({ ...basePayload, Password: value })
          : await adminChangeUserAccessCode({
              ...basePayload,
              AccessCode: value,
            });
      const parsed = parseAdminCredentialChangeResponse(response);

      setPendingAdminCred(null);
      setAdminCredAuthOpen(false);

      if (parsed.ok) {
        showAlert({
          title: "Success",
          message: parsed.message,
          type: "success",
        });
      } else {
        showAlert({
          title: "Error",
          message: parsed.message,
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Could not update credentials.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const listMenuItems = useMemo(() => {
    const items = [
      {
        label: "View profile",
        icon: "fas fa-id-card",
        onClick: (row) =>
          setProfileModalStaffId(String(row?.StaffId || "")),
      },
    ];
    if (canManage) {
      items.push(
        { divider: true },
        {
          label: (row) =>
            isListUserActive(row) ? "Deactivate" : "Activate",
          icon: (row) =>
            isListUserActive(row) ? "fas fa-ban" : "fas fa-check-circle",
          onClick: handleToggleStatusClick,
        },
      );
    }
    if (canChangeCredentials) {
      items.push({
        label: "Change credentials",
        icon: "fas fa-key",
        onClick: handleAdminCredAction,
        visible: (row) => isListUserActive(row),
      });
    }
    return items;
  }, [
    canManage,
    canChangeCredentials,
    handleToggleStatusClick,
    handleAdminCredAction,
    isListUserActive,
  ]);

  const buildStaffSnapshotFromRecord = useCallback(
    (staff, studiesForForm = []) => {
      if (!staff) return null;
      const mobileCountryIso2 = String(staff.MobileCountryIso2 || "GB")
        .trim()
        .toUpperCase();
      return {
        firstName: staff.Forename?.trim() || "",
        lastName: staff.Surname?.trim() || "",
        employeeNumber: String(staff["Employee Number"] ?? "").trim(),
        email: String(staff["Email Id"] ?? "").trim(),
        contactNumber: sanitizeNationalMobileInput(
          staff["Contact Number"] || "",
          mobileCountryIso2,
        ),
        mobileCountryIso2,
        gender: staff.Gender || staff.gender || "",
        startDate: staff["Start Date"] ? new Date(staff["Start Date"]) : null,
        accessDate: staff["Access Date"]
          ? new Date(staff["Access Date"])
          : null,
        dob: staff["Date of birth"]
          ? new Date(utils.parseDDMMYYYY(staff["Date of birth"]))
          : null,
        roleIdRef: String(staff.RoleIdRef || "").trim(),
        departmentId: String(staff.DepartmentId || "").trim(),
        lineManagerId: String(staff.LineManagerId || "").trim(),
        haveLineManagerRights:
          String(staff["Line Manager Rights"] || "")
            .trim()
            .toLowerCase() === "yes",
        addressLine1: staff["H No"] || "",
        city: staff.City || "",
        postalCode: staff["Post Code"] || "",
        county: staff.County || "",
        country: staff.Country || "",
        profilePicture: staff.ProfilePictureUrl || "",
        IsCRO: 1,
        studies: studiesForForm,
      };
    },
    [],
  );

  const fetchStaffSnapshotForSave = useCallback(
    async (staffId) => {
      if (!staffId || !userData?.OrganizationId || !userData?.StaffId) {
        return null;
      }
      const userResponse = await api.post(
        API_ENDPOINTS.USER.GET_INDIVIDUAL_USER,
        {
          OrganizationId: userData.OrganizationId,
          StaffIDRef: userData.StaffId,
          CreatedBy: userData.StaffId,
          StaffId: staffId,
          Action: "GetProfileDetails",
        },
      );
      const staff = Array.isArray(userResponse?.data?.StaffRecord)
        ? userResponse.data.StaffRecord[0]
        : userResponse?.data?.StaffRecord ||
          userResponse?.data?.SuccessStory?.[0] ||
          null;
      if (!staff || !isSponsorCroUserRecord(staff?.IsCRO ?? staff?.IsCro)) {
        return null;
      }
      if (!isStaffProfileActive(staff)) {
        return null;
      }
      const studyOptions = studyOptionsCacheRef.current || studyList || [];
      const generalStudyList = collectAssignedStudyRowsFromProfile(
        userResponse.data,
        staff,
      );
      let entityAccessRaw = extractExternalEntityAccessFromProfile(
        userResponse.data,
        staff,
      );
      try {
        const liveGrants = await fetchExternalEntityAccessGrants({
          organizationId: userData.OrganizationId,
          staffId,
        });
        if (parseExternalEntityAccessRows(liveGrants)?.length) {
          entityAccessRaw = liveGrants;
        }
      } catch {
        // Keep profile grants when GetValid is unavailable.
      }
      const parsedEntityAccess =
        parseExternalEntityAccessRows(entityAccessRaw) || [];
      const mergedStudyNormIds = [
        ...new Set([
          ...generalStudyList
            .map((s) => normListId(pickStudyIdFromRow(s)))
            .filter(Boolean),
          ...parsedEntityAccess
            .map((row) => normListId(row.studyId))
            .filter(Boolean),
        ]),
      ];
      const studiesForForm =
        mergedStudyNormIds.length > 0
          ? mergedStudyNormIds.map((id) => {
              const opt = (studyOptions || []).find(
                (s) => normListId(s.value) === id,
              );
              const fromGeneral = generalStudyList.find(
                (s) => normListId(pickStudyIdFromRow(s)) === id,
              );
              const sid =
                opt?.value ?? pickStudyIdFromRow(fromGeneral) ?? id;
              const studyNumber =
                opt?.studyNumber ||
                fromGeneral?.StudyNumber ||
                fromGeneral?.studyNumber ||
                "";
              const studyName =
                opt?.studyName ||
                fromGeneral?.StudyName ||
                fromGeneral?.studyName ||
                "";
              const label =
                opt?.label ||
                (studyNumber
                  ? `${studyNumber} - ${studyName}`
                  : studyName || fromGeneral?.label || String(sid));
              return {
                StudyId: sid,
                label,
                studyNumber,
                studyName,
                StudyNumber: studyNumber,
                StudyName: studyName,
              };
            })
          : generalStudyList;
      return {
        snapshot: buildStaffSnapshotFromRecord(staff, studiesForForm),
        grantsRaw: entityAccessRaw,
      };
    },
    [
      buildStaffSnapshotFromRecord,
      studyList,
      userData?.OrganizationId,
      userData?.StaffId,
    ],
  );

  const postSaveUpdateUser = useCallback(
    async ({ staffId, snap, studiesForSave, documentsXml, isfDocuments }) => {
      const mobileCountryIso2 = String(snap.mobileCountryIso2 || "GB")
        .trim()
        .toUpperCase();
      const contactResult = validateNationalMobile(snap.contactNumber, {
        countryIso2: mobileCountryIso2,
        required: false,
      });
      const response = await api.post(API_ENDPOINTS.USER.SAVE_UPDATE_USER, {
        StaffId: staffId,
        FirstName: snap.firstName || "",
        LastName: snap.lastName || "",
        EmployeeNum: snap.employeeNumber,
        RoleIdRef: snap.roleIdRef || "",
        Gender: snap.gender || "",
        StartDate: snap.startDate ? formatDate(snap.startDate) : "",
        AccessDate: snap.accessDate ? formatDate(snap.accessDate) : "",
        DOB: snap.dob ? formatDate(snap.dob) : "",
        EmailId: (snap.email || "").trim(),
        ContactNumber: contactResult.national || snap.contactNumber || "",
        MobileCountryIso2: mobileCountryIso2,
        AddressLine1: snap.addressLine1 || "",
        City: snap.city || "",
        PostCode: snap.postalCode || "",
        County: snap.county || "",
        Country: snap.country || "",
        HaveLineManagerRights: snap.haveLineManagerRights ? 1 : 0,
        Action: "Update",
        LineManagerId: snap.lineManagerId || "",
        DepartmentId: snap.departmentId || "",
        StudyList:
          studiesForSave?.map((studyItem) => ({
            StudyId: studyItem.StudyId || studyItem.value,
            StudyName: studyItem.label || studyItem.StudyName || "",
          })) || [],
        OrganizationId: userData?.OrganizationId || "",
        CreatedById: userData?.StaffId || "",
        LoggedInUserName:
          userData?.UserName ||
          `${userData?.FirstName || ""} ${userData?.LastName || ""}`.trim() ||
          "",
        OrganizationName: userData?.OrganizationName || "",
        DocumentList: "",
        Base64Image: "",
        Extension: "",
        ProfilePictureUrl: snap.profilePicture || "",
        IsCRO: 1,
        DocumentsXML: documentsXml,
        ExternalStaffISFDocuments: isfDocuments,
      });
      const storyRow = Array.isArray(response?.data)
        ? response.data[0]
        : response?.data?.SuccessStory?.[0] || response?.data?.[0] || null;
      const isSuccess =
        Number(response?.success) === 1 ||
        Number(storyRow?.Success) === 1 ||
        storyRow?.Success === true;
      return {
        ok: isSuccess,
        message:
          storyRow?.MSG ||
          response?.message ||
          (isSuccess
            ? "Studies updated successfully."
            : "Failed to update study assignments."),
      };
    },
    [userData],
  );

  const saveStudies = async (formOverrides = {}) => {
    if (!canManage) {
      showAlert({
        type: "warning",
        title: "Permission required",
        message: "You do not have permission to update study assignments.",
      });
      return false;
    }

    const shareError = externalScoping.getStudyShareValidationError();
    if (shareError) {
      showAlert({
        title: "Cannot update sharing",
        message: shareError,
        type: "warning",
      });
      return false;
    }

    const isRemoveOrOverride = Array.isArray(formOverrides?.studies);
    const targets = isRemoveOrOverride
      ? selectedStaffId && isProfileUserActive
        ? [String(selectedStaffId)]
        : []
      : (modalAssignStaffIds.length
          ? modalAssignStaffIds
          : assignTargetStaffIds
        )
          .map((id) => String(id || "").trim())
          .filter(Boolean)
          .filter((id) => {
            const row = users.find(
              (u) =>
                String(u?.StaffId || "").toLowerCase() ===
                String(id).toLowerCase(),
            );
            return row && isListUserActive(row);
          });

    if (!targets.length) {
      showAlert({
        type: "warning",
        title: "Select users",
        message:
          "Select at least one active Sponsor/CRO user in the Assign study dialog.",
      });
      return false;
    }

    const focusedOnly =
      targets.length === 1 &&
      String(targets[0]).toLowerCase() ===
        String(selectedStaffId || "").toLowerCase();

    // Single focused user: keep existing full-replace save behaviour.
    if (focusedOnly || isRemoveOrOverride) {
      if (!selectedStaffId || !staffSnapshot) {
        showAlert({
          type: "warning",
          title: "Select a user",
          message: "Choose a Sponsor/CRO user before saving study assignments.",
        });
        return false;
      }
      if (!isProfileUserActive) {
        showAlert({
          type: "warning",
          title: "User inactive",
          message:
            "This user is inactive. Study assignment changes are not allowed.",
        });
        return false;
      }

      const nextStudies = Array.isArray(formOverrides?.studies)
        ? formOverrides.studies
        : studies;

      let documentsXml = "";
      let externalStaffIsfDocumentsPayload = [];
      try {
        [documentsXml, externalStaffIsfDocumentsPayload] = await Promise.all([
          externalScoping.buildCroDocumentListString(),
          externalScoping.buildExternalStaffIsfDocumentsPayload(),
        ]);
      } catch (e) {
        console.error("buildCroDocumentListString", e);
        showAlert({
          title: "Error",
          message: "Could not build document sharing payload. Try again.",
          type: "danger",
        });
        return false;
      }

      try {
        showLoader("Saving studies...");
        const result = await postSaveUpdateUser({
          staffId: selectedStaffId,
          snap: staffSnapshot,
          studiesForSave: nextStudies,
          documentsXml,
          isfDocuments: externalStaffIsfDocumentsPayload,
        });

        if (!result.ok) {
          showAlert({
            title: "Error",
            message: result.message,
            type: "danger",
          });
          return false;
        }

        setStudies(nextStudies || []);
        setStaffSnapshot((prev) =>
          prev ? { ...prev, studies: nextStudies || [] } : prev,
        );

        try {
          const liveGrants = await fetchExternalEntityAccessGrants({
            organizationId: userData?.OrganizationId,
            staffId: selectedStaffId,
          });
          if (liveGrants != null) {
            setExternalEntityAccessFromApi(liveGrants);
            setExternalShareHydrationNonce((n) => n + 1);
          }
        } catch (grantErr) {
          console.error("refresh external entity access after save", grantErr);
        }

        setActivityLogNonce((n) => n + 1);

        showAlert({
          title: "Success",
          message: "Studies updated successfully.",
          type: "success",
        });
        return true;
      } catch (error) {
        console.error("saveStudies", error);
        showAlert({
          title: "Error",
          message:
            error?.message ||
            "Failed to update study assignments. Please try again.",
          type: "danger",
        });
        return false;
      } finally {
        hideLoader();
      }
    }

    // Multi-user (or single non-focused target): delta merge only — never copy full XML.
    let deltaRows = [];
    try {
      deltaRows = await externalScoping.buildAssignDeltaDocumentRows();
    } catch (e) {
      console.error("buildAssignDeltaDocumentRows", e);
      showAlert({
        title: "Error",
        message: "Could not build document sharing payload. Try again.",
        type: "danger",
      });
      return false;
    }

    if (!deltaRows.length) {
      showAlert({
        type: "warning",
        title: "Nothing to assign",
        message: "Select a study and site to assign before saving.",
      });
      return false;
    }

    let successCount = 0;
    let failCount = 0;
    const failMessages = [];

    try {
      showLoader(
        targets.length > 1
          ? `Saving studies for ${targets.length} users...`
          : "Saving studies...",
      );

      for (const staffId of targets) {
        try {
          const loaded = await fetchStaffSnapshotForSave(staffId);
          if (!loaded?.snapshot) {
            failCount += 1;
            failMessages.push(`Could not load profile for user ${staffId}.`);
            continue;
          }

          let existingRows = [];
          try {
            const liveGrants = await fetchExternalEntityAccessGrants({
              organizationId: userData?.OrganizationId,
              staffId,
            });
            existingRows = externalEntityAccessToDocumentRows(
              liveGrants ?? loaded.grantsRaw,
            );
          } catch {
            existingRows = externalEntityAccessToDocumentRows(loaded.grantsRaw);
          }

          const mergedRows = mergeDocumentRowsWithDelta(
            existingRows,
            deltaRows,
          );
          const documentsXml = documentRowsToDocumentsXml(mergedRows);
          const isfDocuments =
            documentRowsToExternalStaffIsfDocuments(mergedRows);

          const studyIdSet = new Map();
          (loaded.snapshot.studies || []).forEach((row) => {
            const sid = String(row?.StudyId || row?.value || "").trim();
            if (sid) studyIdSet.set(sid.toLowerCase(), row);
          });
          deltaRows.forEach((row) => {
            const sid = String(row?.studyId || "").trim();
            if (!sid || studyIdSet.has(sid.toLowerCase())) return;
            const opt = (studyList || []).find(
              (s) =>
                String(s?.value || "").toLowerCase() === sid.toLowerCase(),
            );
            studyIdSet.set(sid.toLowerCase(), {
              StudyId: sid,
              label: opt?.label || sid,
              StudyName: opt?.studyName || opt?.label || sid,
            });
          });
          const studiesForSave = [...studyIdSet.values()];

          const result = await postSaveUpdateUser({
            staffId,
            snap: loaded.snapshot,
            studiesForSave,
            documentsXml,
            isfDocuments,
          });
          if (result.ok) {
            successCount += 1;
          } else {
            failCount += 1;
            failMessages.push(result.message);
          }
        } catch (err) {
          console.error("multi saveStudies", staffId, err);
          failCount += 1;
          failMessages.push(
            err?.message || `Failed to update user ${staffId}.`,
          );
        }
      }

      if (selectedStaffId) {
        await loadSelectedUser(selectedStaffId);
      }
      setActivityLogNonce((n) => n + 1);

      if (failCount === 0) {
        setModalAssignStaffIds([]);
        showAlert({
          title: "Success",
          message:
            successCount === 1
              ? "Studies updated successfully."
              : `Studies assigned successfully for ${successCount} users.`,
          type: "success",
        });
        return true;
      }

      showAlert({
        title: successCount > 0 ? "Partially completed" : "Error",
        message:
          successCount > 0
            ? `Updated ${successCount} user(s); ${failCount} failed.${
                failMessages[0] ? ` ${failMessages[0]}` : ""
              }`
            : failMessages[0] ||
              "Failed to update study assignments. Please try again.",
        type: successCount > 0 ? "warning" : "danger",
      });
      return successCount > 0;
    } catch (error) {
      console.error("saveStudies multi", error);
      showAlert({
        title: "Error",
        message:
          error?.message ||
          "Failed to update study assignments. Please try again.",
        type: "danger",
      });
      return false;
    } finally {
      hideLoader();
    }
  };

  const closeProfileModal = useCallback(() => {
    const closedId = profileModalStaffId;
    setProfileModalStaffId("");
    if (
      closedId &&
      String(closedId).toLowerCase() ===
        String(selectedStaffId || "").toLowerCase()
    ) {
      loadSelectedUser(closedId);
    }
  }, [loadSelectedUser, profileModalStaffId, selectedStaffId]);

  if (!featureEnabled) {
    return (
      <div className="cro-study-assignment cro-study-assignment-page">
        <PageBreadcrumb items={breadcrumbItems} />
        <div className="alert alert-warning mb-0">
          Sponsor/CRO management is not enabled for this organisation. Please
          contact the AQ team to enable it.
        </div>
        <InfoAlert {...alertState} onClose={hideAlert} />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="cro-study-assignment cro-study-assignment-page">
        <PageBreadcrumb items={breadcrumbItems} />
        <div className="alert alert-warning mb-0">
          You do not have permission to manage Sponsor/CRO study assignments.
        </div>
        <InfoAlert {...alertState} onClose={hideAlert} />
      </div>
    );
  }

  return (
    <div className="cro-study-assignment cro-study-assignment-page">
      <PageBreadcrumb
        items={breadcrumbItems}
        preActionsExtras={breadcrumbPreActions}
        actions={breadcrumbActions}
      />

      <div className="cro-study-assignment__layout">
        <aside className="cro-study-assignment__list-pane">
          <div className="cro-study-assignment__list-head">
            <h2 className="cro-study-assignment__list-title">
              Sponsor / CRO users
            </h2>
            <div className="cro-study-assignment__search-row">
              <input
                type="search"
                className="form-control form-control-sm cro-study-assignment__search"
                placeholder="Search name, employee #"
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
                aria-label="Search by name or employee number"
              />
              <button
                type="button"
                className="cro-study-assignment__list-refresh"
                onClick={() => loadCroUsers()}
                disabled={usersLoading}
                title="Refresh users"
                aria-label="Refresh Sponsor/CRO users"
              >
                <i className="fas fa-sync-alt" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div
            className="cro-study-assignment__list-body"
            ref={listBodyRef}
            onScroll={(event) => {
              listScrollTopRef.current = event.currentTarget.scrollTop;
            }}
          >
            {usersLoading ? (
              <div className="cro-study-assignment__empty">Loading users…</div>
            ) : filteredUsers.length === 0 ? (
              <div className="cro-study-assignment__empty">
                No Sponsor/CRO users match this search.
              </div>
            ) : (
              filteredUsers.map((row) => {
                const id = String(row?.StaffId || "");
                const selected =
                  id.toLowerCase() ===
                  String(selectedStaffId || "").toLowerCase();
                return (
                  <CroSponsorUserListItem
                    key={id}
                    row={row}
                    selected={selected}
                    onSelect={() => selectUser(id)}
                    menuItems={listMenuItems}
                  />
                );
              })
            )}
          </div>
        </aside>
        <section className="cro-study-assignment__detail-pane">
          <div className="cro-study-assignment__detail-tabs" role="tablist">
            <div
              className={`cro-study-assignment__tab-item${
                detailTab === "studies" ? " is-active" : ""
              }`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={detailTab === "studies"}
                className={`cro-study-assignment__tab${
                  detailTab === "studies" ? " is-active" : ""
                }`}
                onClick={() => setDetailTab("studies")}
              >
                Assigned studies
              </button>
              {detailTab === "studies" && selectedStaffId ? (
                <button
                  type="button"
                  className="cro-study-assignment__tab-refresh"
                  title="Refresh assigned studies"
                  aria-label="Refresh assigned studies"
                  onClick={() => {
                    if (selectedStaffId) loadSelectedUser(selectedStaffId);
                  }}
                  disabled={profileLoading}
                >
                  <i className="fas fa-sync-alt" aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <div
              className={`cro-study-assignment__tab-item${
                detailTab === "activity" ? " is-active" : ""
              }`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={detailTab === "activity"}
                className={`cro-study-assignment__tab${
                  detailTab === "activity" ? " is-active" : ""
                }`}
                onClick={() => setDetailTab("activity")}
                disabled={!selectedStaffId}
              >
                Study Log
              </button>
              {detailTab === "activity" && selectedStaffId ? (
                <button
                  type="button"
                  className="cro-study-assignment__tab-refresh"
                  title="Refresh log"
                  aria-label="Refresh study log"
                  onClick={() => setActivityLogNonce((n) => n + 1)}
                >
                  <i className="fas fa-sync-alt" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>
          <div className="cro-study-assignment__detail-body">
            {!selectedStaffId ? (
              <div className="cro-study-assignment__placeholder cro-study-assignment__placeholder--panel">
                Select a Sponsor or CRO user to view assignments, or use Assign
                study to share with one or more users.
              </div>
            ) : null}
            <div
              className="cro-study-assignment__tab-panel"
              hidden={!selectedStaffId || detailTab !== "studies"}
            >
              <ExternalAssignedStudiesPanel
                staffId={selectedStaffId || "assign-host"}
                canEdit={canManage}
                hideAssignToolbar
                assignRequestNonce={assignRequestNonce}
                assignedRows={
                  selectedStaffId
                    ? externalScoping.getAssignedStudyReportRows()
                    : []
                }
                loading={
                  Boolean(selectedStaffId) &&
                  (profileLoading || externalScoping.assignedTableLoading)
                }
                assignUserOptions={activeUsersForAssign}
                assignSelectedStaffIds={modalAssignStaffIds}
                onAssignSelectedStaffIdsChange={setModalAssignStaffIds}
                renderAssignStep={externalScoping.renderStudiesStep}
                onBeginAssign={externalScoping.beginAssignSession}
                onCancelAssign={externalScoping.cancelAssignSession}
                onCommitAssign={externalScoping.commitAssignSession}
                onSaveAssign={() => saveStudies()}
                getAssignShareError={externalScoping.getStudyShareValidationError}
                onRemoveStudy={
                  selectedStaffId && isProfileUserActive
                    ? async (rowOrStudyId) => {
                        const nextStudies =
                          rowOrStudyId && typeof rowOrStudyId === "object"
                            ? externalScoping.unshareAssignedGrant(rowOrStudyId)
                            : externalScoping.unshareAssignedStudy(rowOrStudyId);
                        return saveStudies({ studies: nextStudies });
                      }
                    : undefined
                }
              />
            </div>
            <div
              className="cro-study-assignment__tab-panel cro-study-assignment__activity"
              hidden={!selectedStaffId || detailTab !== "activity"}
            >
              {selectedStaffId ? (
                <ExternalAccessActivityLog
                  organizationId={userData?.OrganizationId}
                  staffId={selectedStaffId}
                  refreshNonce={activityLogNonce}
                  active={detailTab === "activity"}
                />
              ) : null}
            </div>
          </div>
        </section>
      </div>

      <InfoAlert {...alertState} onClose={hideAlert} />

      <SponsorCroUserAddModal
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        userData={userData}
        showAlert={showAlert}
        onSaved={async (savedStaffId) => {
          await loadCroUsers();
          if (savedStaffId) selectUser(savedStaffId);
        }}
      />

      <ConfirmModal
        isOpen={statusConfirm.open}
        onClose={handleStatusConfirmClose}
        onConfirm={handleStatusConfirmYes}
        title={
          statusConfirm.action === "deactivate"
            ? "Deactivate user"
            : "Activate user"
        }
        message={
          statusConfirm.row
            ? `Do you want to ${
                statusConfirm.action === "deactivate"
                  ? "deactivate"
                  : "activate"
              } ${formatDisplayName(statusConfirm.row) || "this user"}?`
            : ""
        }
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass={
          statusConfirm.action === "deactivate" ? "btn-danger" : "btn-success"
        }
      />

      <AuthModal
        show={statusAuthOpen}
        onClose={handleStatusAuthClose}
        onSubmit={handleStatusAuthSubmit}
        showDescription={false}
      />

      <AdminChangeUserCredentialsModal
        open={adminCredForm.open}
        targetUser={adminCredForm.row}
        onClose={handleAdminCredFormClose}
        onSubmit={handleAdminCredFormSubmit}
      />

      <ConfirmModal
        isOpen={adminCredConfirm.open}
        onClose={handleAdminCredConfirmClose}
        onConfirm={handleAdminCredConfirmYes}
        title={
          adminCredConfirm.mode === "accessCode"
            ? "Change access code"
            : "Change password"
        }
        message={
          adminCredConfirm.row
            ? `Are you sure you want to change the ${
                adminCredConfirm.mode === "accessCode"
                  ? "access code"
                  : "password"
              } for ${formatDisplayName(adminCredConfirm.row) || "this user"}?`
            : ""
        }
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass="btn-primary"
      />

      <AuthModal
        show={adminCredAuthOpen}
        onClose={handleAdminCredAuthClose}
        onSubmit={handleAdminCredAuthSubmit}
        showDescription={false}
      />

      <CtmsWorkspaceModal
        open={Boolean(profileModalStaffId)}
        title="User Profile"
        titleId="cro-sponsor-profile-title"
        onClose={closeProfileModal}
        className="cro-profile-workspace-modal"
        dialogClassName="ctms-workspace-modal__dialog--full"
        contentClassName="cro-profile-workspace-modal__content"
      >
        {profileModalStaffId ? (
          <UserProfile
            key={profileModalStaffId}
            embedded
            embeddedStaffId={profileModalStaffId}
            onClose={closeProfileModal}
          />
        ) : null}
      </CtmsWorkspaceModal>
    </div>
  );
}
